import { useState, type ReactNode } from "react";
import { Dialog, DialogTrigger, Pressable } from "react-aria-components";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/helpers";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";

/**
 * The scrolled header: what the bar becomes once the view header has scrolled
 * away. One row: the view's name, a hairline, and a FILTERS facet that sums up
 * every active filter and opens them all in one glass panel; the search sits
 * at the right. Nothing else, so nothing overlaps at any width the bar runs at.
 */
export default function WallScrolledRow({
  title,
  summary,
  panel,
  search,
  leading,
  trailing,
}: {
  title: string;
  /** One word per active filter, in group order. Empty reads NONE. */
  summary: readonly string[];
  /** The filter panel, drawn inside the facet's popover. */
  panel: ReactNode;
  search: ReactNode;
  /** Facets before FILTERS (References: the Collection facet). */
  leading?: ReactNode;
  /** After the search (References: the readout and the Save icon). */
  trailing?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const value = summary.length > 0 ? summary.join(" · ") : "None";

  return (
    <div className="trend-scrolled">
      {/* The page's h1 still names the view for assistive technology. */}
      <span className="trend-scrolled-title" aria-hidden="true">
        {title}
      </span>
      <span className="trend-scrolled-divider" aria-hidden="true" />
      {leading}
      <DialogTrigger isOpen={open} onOpenChange={setOpen}>
        <Pressable>
          <button
            type="button"
            className="wb-facet trend-filters-face"
            aria-label={`Filters, ${summary.length > 0 ? summary.join(", ") : "none set"}`}
          >
            <span className="wb-caption wb-caption--facet" aria-hidden="true">
              Filters
            </span>
            <span
              className={cn("wb-facet-value", summary.length === 0 && "wb-facet-value--any")}
              title={value}
            >
              {value}
            </span>
            <ChevronDown className="wb-facet-chev" strokeWidth={2.5} aria-hidden="true" />
          </button>
        </Pressable>
        <KitPopover
          placement="bottom start"
          /* 6px under the 52px bar rather than under the face (library). */
          offset={16}
          className="wb-glass-pop trend-filter-pop w-[360px] max-w-[calc(100vw-2rem)] p-0"
        >
          <Dialog aria-label="Filters" className="outline-none">
            {panel}
          </Dialog>
        </KitPopover>
      </DialogTrigger>
      <div className="trend-scrolled-search">{search}</div>
      {trailing}
    </div>
  );
}
